import { Button } from "@/components/ui/button";
import { Brain, FileText, TrendingUp, CheckCircle, Star, Users, Target, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

const Home = () => {
  const navigate = useNavigate();
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);

  const heroImages = [
    "/Screenshot 2026-03-16 191914.png",
    "/Screenshot 2026-03-16 191932.png",
    "/Screenshot 2026-03-16 191954.png",
    "/Screenshot 2026-03-16 192022.png",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const fadeInUp: any = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const staggerContainer: any = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const scaleIn: any = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };


  const features = [
    {
      icon: Brain,
      title: "AI-Powered Analysis",
      description: "Advanced machine learning algorithms trained on UPSC patterns to provide accurate, consistent evaluations.",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Get comprehensive feedback within seconds, not days. Practice more, improve faster.",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Target,
      title: "UPSC-Focused Criteria",
      description: "Evaluation based on actual UPSC marking schemes including structure, content, and presentation.",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: TrendingUp,
      title: "Progress Tracking",
      description: "Visual analytics and insights to track your improvement journey over time.",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: Star,
      title: "Detailed Feedback",
      description: "Question-wise breakdown with specific suggestions for improvement and model answers.",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: FileText,
      title: "Export Reports",
      description: "Download professional PDF reports to review offline or share with mentors.",
      color: "from-indigo-500 to-violet-600"
    }
  ];

  const steps = [
    {
      step: "01",
      title: "Upload Your Answer",
      description: "Simply upload your handwritten or typed answer sheet in PDF format.",
      icon: FileText
    },
    {
      step: "02",
      title: "AI Evaluation",
      description: "Our AI analyzes your answer against UPSC standards and marking criteria.",
      icon: Brain
    },
    {
      step: "03",
      title: "Get Detailed Feedback",
      description: "Receive comprehensive feedback with scores, suggestions, and model answers.",
      icon: CheckCircle
    }
  ];

  const testimonials = [
    {
      quote: "I used to struggle with structuring my answers. Parakh didn't just give me marks; it taught me how to think like an examiner.",
      name: "Rahul Sharma",
      role: "UPSC CSE 2024 Aspirant",
      stat1: "+45%",
      label1: "Improvement in GS Score",
      stat2: "3 Mo.",
      label2: "To consistent answer writing",
      initial: "R"
    },
    {
      quote: "The instant feedback loop is a game changer. I wrote 3 answers a day and got them reviewed immediately. My speed improved drastically.",
      name: "Priya Singh",
      role: "State PSC Topper",
      stat1: "2x",
      label1: "Writing Speed",
      stat2: "100+",
      label2: "Answers Practiced",
      initial: "P"
    },
    {
      quote: "As someone from a tier-3 city without access to expensive coaching, Parakh leveled the playing field for me. Quality feedback at ₹0.44 per question is unbelievable. My answer writing has improved drastically!",
      name: "Yash Shukla",
      role: "UPSC CSE 2024 Aspirant",
      stat1: "+45%",
      label1: "Improvement in GS Score",
      stat2: "3 Mo.",
      label2: "To consistent answer writing",
      initial: "Y"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000); // Rotate every 6 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen overflow-hidden">
      <section style={{ backgroundImage: "url('/herobg.webp')" }} className="relative bg-black min-h-screen flex items-center m-2 p-4 md:m-4 rounded-2xl mt-0">

        <div className="relative max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* Left — Rotating Images */}
          <motion.div
            className="relative flex justify-center items-center order-2 lg:order-1"
            initial={{ opacity: 0, x: -40 }}
            animate={{
              opacity: 1,
              x: 0,
              y: [0, -10, 0]
            }}
            transition={{
              opacity: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
              x: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
              y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
            }}
          >
            <motion.div
              whileHover={{ y: -5 }}
              className="relative w-[280px] sm:w-[320px] md:w-[380px] aspect-[2.8/4] rounded-[32px] p-4 bg-white/10 backdrop-blur-xl border border-white/20 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)]"
            >
              <div className="relative w-full h-full rounded-2xl overflow-hidden bg-[#fdfdfd] shadow-inner p-6">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentHeroImage}
                    src={heroImages[currentHeroImage]}
                    alt="Handwritten UPSC answer sheet"
                    className="w-full h-full object-contain"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.5 }}
                  />
                </AnimatePresence>

                {/* Premium AI Scan Effect Overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Glass Reflection */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-50" />

                  {/* Scanning Beam */}
                  <motion.div
                    className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent shadow-[0_0_20px_rgba(99,102,241,0.8)] z-10"
                    animate={{ top: ["0%", "100%", "0%"] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  />

                  {/* Digital Corner Markers */}
                  <div className="absolute top-4 left-4 w-5 h-5 border-t border-l border-indigo-500/30" />
                  <div className="absolute top-4 right-4 w-5 h-5 border-t border-r border-indigo-500/30" />
                  <div className="absolute bottom-4 left-4 w-5 h-5 border-b border-l border-indigo-500/30" />
                  <div className="absolute bottom-4 right-4 w-5 h-5 border-b border-r border-indigo-500/30" />

                  {/* Subtle Grid overlay for scanning feel */}
                  <div className="absolute inset-0 bg-[radial-gradient(#00000005_1px,transparent_1px)] bg-[size:20px_20px] opacity-50" />
                </div>
              </div>
            </motion.div>
            {/* Dots indicator */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {heroImages.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentHeroImage ? "bg-white w-6" : "bg-white/30"}`}
                />
              ))}
            </div>
          </motion.div>

          {/* Right — Text Content */}
          <motion.div
            className="relative w-full space-y-6 text-left order-1 lg:order-2"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Heading */}
            <motion.div variants={fadeInUp}>
              <p className="text-base sm:text-lg md:text-xl font-bold text-white uppercase tracking-[0.12em] mb-2">UPSC & MPPSC Mains</p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.05] text-white drop-shadow-2xl">
                Subjective Copy<br />Evaluation
              </h1>
            </motion.div>

            {/* Subheading */}
            {/* <motion.p variants={fadeInUp} className="text-base sm:text-lg md:text-xl text-white/70 max-w-xl font-light leading-relaxed">
              Get detailed feedback on 15+ parameters, model answers, and actionable insights — all in under 3 minutes.
            </motion.p> */}

            {/* Stats Strip */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-6 sm:gap-10 pt-4">
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white">2,00,000+</div>
                <div className="text-xs sm:text-sm text-white/60 font-medium">Answers Evaluated</div>
              </div>
              <div className="w-px h-10 bg-white/20 hidden sm:block" />
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white">98%</div>
                <div className="text-xs sm:text-sm text-white/60 font-medium">Accuracy</div>
              </div>
              <div className="w-px h-10 bg-white/20 hidden sm:block" />
              <div>
                <div className="text-2xl sm:text-3xl font-bold text-white">3 min</div>
                <div className="text-xs sm:text-sm text-white/60 font-medium">Avg. Result Time</div>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-3 pt-6">
              <motion.div className="w-full sm:w-auto">
                <Button
                  size="lg"
                  onClick={() => navigate("/evaluate")}
                  className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-10 text-base sm:text-lg bg-white text-indigo-950 hover:bg-indigo-50 font-bold rounded-full shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_-15px_rgba(255,255,255,0.5)] transition-all duration-300"
                >
                  Start Evaluating
                </Button>
              </motion.div>

              <motion.div className="w-full sm:w-auto">
                <Button
                  size="lg"
                  onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                  className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-10 text-base sm:text-lg bg-black/20 backdrop-blur-xl border border-white/10 text-white hover:bg-white/10 font-semibold rounded-full transition-all duration-300"
                >
                  See How It Works
                </Button>
              </motion.div>
            </motion.div>

            {/* Social Proof */}
            <motion.div variants={fadeInUp} className="pt-4 flex items-center gap-2 text-sm text-white/50 font-medium">
              <Users className="w-4 h-4" />
              <span>Trusted by 1000+ UPSC & MPPSC aspirants for daily answer practice.</span>
            </motion.div>
          </motion.div>

        </div>
      </section>

      {/* Stats Section - commented out */}
      {/* <section className="container py-20">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="h-full p-8 rounded-[32px] border border-gray-100 bg-white hover:border-gray-200 transition-all duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] group">
                  <div className="w-12 h-12 mb-6 rounded-2xl bg-gray-50 flex items-center justify-center group-hover:bg-indigo-50 transition-colors duration-300">
                    <stat.icon className="w-5 h-5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tighter mb-3">
                    {stat.number}
                  </div>
                  <div className="text-sm font-medium text-gray-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section> */}

      {/* explaination video */}
      <section className="container py-16">
        <motion.div
          className="max-w-5xl mx-auto text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-12">
            <span className="text-sm font-bold text-indigo-600 uppercase tracking-widest">
              Demo
            </span>
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1]">
              See Parakh in <span className="text-indigo-600">Action</span>
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light leading-relaxed">
              Watch how our AI analyzes your handwritten answers to provide detailed feedback in seconds.
            </p>
          </motion.div>

          <motion.div
            variants={scaleIn}
            className="relative rounded-[32px] overflow-hidden shadow-2xl border border-gray-100 bg-white p-2 md:p-4"
          >
            <div className="relative aspect-video rounded-[24px] overflow-hidden bg-gray-100">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/r12xTi_1H2M?si=rMddR_sOS1jpx-J8"
                title="Parakh Demo Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              ></iframe>
            </div>

            {/* Decorative elements */}
            <div className="absolute -z-10 top-0 left-0 w-full h-full bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-blue-500/5 blur-3xl opacity-50" />
          </motion.div>
        </motion.div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="container py-24">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-20 flex justify-center items-center flex-col text-center">
            <span className="text-sm font-bold text-indigo-600 uppercase tracking-widest pl-1">
              Process
            </span>
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1]">
              Effortless evaluation <span className="text-indigo-600">in 3 steps.</span>
            </h2>
            <p className="text-xl text-gray-500 leading-relaxed font-light">
              We've streamlined the entire process so you can focus on writing, while we handle the analysis.
            </p>
          </motion.div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-[60px] left-[15%] right-[15%] h-[2px] bg-gradient-to-r from-gray-200 via-indigo-100 to-gray-200 z-0" />

            <div className="grid md:grid-cols-3 gap-12 relative z-10">
              {steps.map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                  className="relative group"
                >
                  <div className="h-full bg-white p-8 rounded-[32px] border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)] transition-all duration-300">
                    <div className="relative mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center border border-gray-100 group-hover:bg-indigo-600 transition-all duration-500 z-10 relative">
                        <span className="text-xl font-bold text-gray-400 group-hover:text-white transition-colors duration-300">{item.step}</span>
                      </div>
                      <div className="absolute top-1/2 left-full w-[calc(100%+3rem)] h-[2px] bg-gray-100 -translate-y-1/2 -z-10 hidden md:block last:hidden" />
                    </div>

                    <h3 className="text-2xl font-bold mb-3 text-gray-900">{item.title}</h3>
                    <p className="text-gray-500 font-medium leading-relaxed">{item.description}</p>

                    <div className="mt-6 pt-6 border-t border-gray-50 flex items-center gap-2 text-indigo-600 font-semibold text-sm opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                      <item.icon className="w-4 h-4" />
                      <span>View Details</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="container py-24">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-20 flex justify-center items-center flex-col text-center">
            <span className="text-sm font-bold text-indigo-600 uppercase tracking-widest pl-1">
              Features
            </span>
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1]">
              Everything you need to <span className="text-indigo-600">excel in mains.</span>
            </h2>
            <p className="text-xl text-gray-500 leading-relaxed font-light">
              Comprehensive tools designed specifically for UPSC aspirants to practice, evaluate, and improve with precision.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="group h-full p-10 rounded-[32px] bg-gray-50 border border-transparent hover:border-gray-200 hover:bg-white hover:shadow-xl transition-all duration-500">
                  <div className="w-14 h-14 rounded-2xl bg-white border border-gray-100 flex items-center justify-center mb-8 shadow-sm group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <feature.icon className="w-6 h-6 text-gray-900 group-hover:text-indigo-600 transition-colors" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 tracking-tight">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed font-medium">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>



      {/* Success Story Section */}
      <section className="container px-2 py-24">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="mb-16 flex justify-center items-center flex-col text-center">
            <span className="text-sm font-bold text-indigo-600 uppercase tracking-widest pl-1">
              Impact
            </span>
            <h2 className="text-5xl lg:text-7xl font-bold text-gray-900 tracking-tighter leading-[1.1]">
              Real journeys, <span className="text-indigo-600">real results.</span>
            </h2>
          </motion.div>

          <motion.div variants={scaleIn}>
            <div className="relative rounded-[40px] overflow-hidden bg-[#0f1115] text-white p-8 md:p-16 shadow-2xl min-h-[500px] flex items-center">
              {/* Background texture/gradient */}
              <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-indigo-900/20 via-transparent to-transparent z-0" />
              <div className="absolute bottom-0 left-0 w-2/3 h-2/3 bg-gradient-to-tr from-purple-900/10 via-transparent to-transparent z-0" />

              <div className="relative z-10 w-full">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTestimonial}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.5 }}
                    className="grid lg:grid-cols-2 gap-12 items-center w-full"
                  >
                    <div className="space-y-10">
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="text-sm font-medium text-gray-200">Featured Story {currentTestimonial + 1}/{testimonials.length}</span>
                      </div>

                      <blockquote className="text-2xl md:text-3xl lg:text-4xl font-medium leading-normal tracking-tight">
                        "{testimonials[currentTestimonial].quote}"
                      </blockquote>

                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-lg font-bold">
                          {testimonials[currentTestimonial].initial}
                        </div>
                        <div className="text-lg">
                          <div className="font-bold text-white">{testimonials[currentTestimonial].name}</div>
                          <div className="text-indigo-200/80 text-sm">{testimonials[currentTestimonial].role}</div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row lg:flex-col gap-6">
                      <div className="flex-1 p-8 rounded-[32px] bg-white/5 border border-white/10 backdrop-blur-md hover:bg-white/10 transition-colors duration-300">
                        <div className="text-5xl font-bold text-white mb-2 tracking-tighter">{testimonials[currentTestimonial].stat1}</div>
                        <div className="text-gray-400 font-medium">{testimonials[currentTestimonial].label1}</div>
                      </div>
                      <div className="flex-1 p-8 rounded-[32px] bg-white/5 border border-white/10 backdrop-blur-md hover:bg-white/10 transition-colors duration-300">
                        <div className="text-5xl font-bold text-white mb-2 tracking-tighter">{testimonials[currentTestimonial].stat2}</div>
                        <div className="text-gray-400 font-medium">{testimonials[currentTestimonial].label2}</div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="container px-2 py-24 pb-32">
        <motion.div
          className="max-w-7xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.div variants={scaleIn}>
            <div className="rounded-[40px] bg-indigo-600 overflow-hidden shadow-2xl relative">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* Text Content */}
                <div className="p-10 md:p-16 space-y-8 text-left z-10">
                  <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
                    From answer writing <br />
                    to <span className="text-indigo-200">UPSC selection.</span>
                  </h2>
                  <p className="text-lg text-indigo-100/90 font-light max-w-lg leading-relaxed">
                    The first and last platform you'll ever need. We guide you to the best way to improved scores and selection.
                    <br /><br />
                    Got a target rank?
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 pt-2">
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        size="lg"
                        onClick={() => navigate("/evaluate")}
                        className="h-14 px-8 text-base bg-white text-indigo-600 hover:bg-white/90 font-bold rounded-xl shadow-lg transition-all duration-300 w-full sm:w-auto"
                      >
                        Get Started
                      </Button>
                    </motion.div>

                    <motion.div whileHover={{ x: 5 }} className="flex items-center">
                      <Button
                        variant="ghost"
                        onClick={() => navigate("/about-us")}
                        className="h-14 px-6 text-base text-white hover:text-white hover:bg-white/10 font-medium rounded-xl transition-all duration-300 w-full sm:w-auto group"
                      >
                        Learn more <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
                      </Button>
                    </motion.div>
                  </div>
                </div>

                {/* UI Image Representation */}
                <div className="relative h-full min-h-[400px] lg:min-h-full bg-indigo-700/50 lg:bg-transparent overflow-hidden">
                  {/* This is a placeholder for the UI screenshot. Using herobg.jpg or similar. */}
                  {/* We position it to look like it's sliding in or part of a dashboard */}
                  <div className="absolute top-10 lg:top-16 left-10 lg:left-0 w-full h-full rounded-tl-[32px] overflow-hidden shadow-2xl border-t-8 border-l-8 border-gray-900 bg-gray-900">


                    <motion.img
                      initial={{ x: 100, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 0.8 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      src="/ctaimg.webp"
                      alt="Platform Interface"
                      className="w-full h-full object-cover object-left hover:opacity-100 transition-opacity duration-500"
                    />

                    {/* Overlay to simulate UI depth if image isn't perfect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-transparent pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>
    </div >
  );
};

export default Home;
